import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SERVER_DATE_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { WORKFLOW_USAGE_DETAILS_REFERENCE_DATA_TYPES, WorkflowUsageDetailsReferenceData } from "@constants/reference-data";
import { TimeRangeTypes } from "@enums/time-range-types";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { WorkflowUsageDetailsSearchFiltersState } from "@idw-types/state/workflow-usage-details-state";
import { setWorkflowUsageDetailsSearchFilters } from "@redux/actions/workflow-usage-details-actions";
import { initialWorkflowUsageDetailsState } from "@redux/reducers/workflow-usage-details-reducer";
import { getWorkflowUsageDetailsSearchResultsReduxRequest } from "@redux-requests/workflow-usage-details";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { WorkflowUsageDetailsSearchForm } from "./workflow-usage-details-search-form/workflow-usage-details-search-form";
import { ServiceOfferingTransactionsSummaryTableContainer } from "./service-offering-transactions-summary-table-container/service-offering-transactions-summary-table-container";
import { UserTransactionsSummaryTableContainer } from "./user-transactions-summary-table-container/user-transactions-summary-table-container";
import { WorkflowSankeyDiagram } from "./workflow-sankey-diagram/workflow-sankey-diagram";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { getSessionState, getWorkflowUsageDetailsState } from "@redux/selectors/root-selectors";

export const WorkflowUsageDetailsContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { searchResults, searchFilters } = useSelector(getWorkflowUsageDetailsState);
    const referenceData = useReferenceDataSelector<WorkflowUsageDetailsReferenceData>(WORKFLOW_USAGE_DETAILS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.workflowUsageDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                WORKFLOW_USAGE_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.WORKFLOW_USAGE_DETAILS
            );
        }
    }, [referenceData.isLoading.workflowUsageDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnRelyingParties &&
            referenceData.simpleAXNRPServices &&
            referenceData.axnServiceOfferings &&
            referenceData.simpleAXNWorkflows
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnRelyingParties,
        referenceData.simpleAXNRPServices,
        referenceData.axnServiceOfferings,
        referenceData.simpleAXNWorkflows
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.workflowUsageDetails;
    }, [referenceData.isLoading.workflowUsageDetails]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const allowedRelyingParties = useMemo(() => {
        if (referenceData.axnRelyingParties) {
            return extractRelyingPartiesForPropertyName(
                UserPermissionTypes.VIEW_WORKFLOW_USAGE_DETAILS,
                session.user.axnAdminUserRPProperties,
                referenceData.axnRelyingParties
            );
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        referenceData.axnRelyingParties
    ]);

    const allowedWorkflows = useMemo(() => {
        if (referenceData.simpleAXNWorkflows) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.simpleAXNWorkflows.filter(workflow => rpIds.includes(workflow.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.simpleAXNWorkflows
    ]);

    const allowedServices = useMemo(() => {
        if (referenceData.simpleAXNRPServices) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.simpleAXNRPServices.filter(service => rpIds.includes(service.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.simpleAXNRPServices
    ]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: WorkflowUsageDetailsSearchFiltersState = {
                customizeBySearchType: (searchParams.get("customizeBySearchType") || initialWorkflowUsageDetailsState.searchFilters.customizeBySearchType) as CustomizeBySearchTypes,
                companyId: searchParams.get("companyId") || initialWorkflowUsageDetailsState.searchFilters.companyId,
                relyingPartyId: searchParams.get("relyingPartyId") || initialWorkflowUsageDetailsState.searchFilters.relyingPartyId,
                rpServiceId: searchParams.get("rpServiceId") || initialWorkflowUsageDetailsState.searchFilters.rpServiceId,
                workflowId: searchParams.get("workflowId") || initialWorkflowUsageDetailsState.searchFilters.workflowId,
                timeRangeType: (searchParams.get("timeRangeType") || initialWorkflowUsageDetailsState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialWorkflowUsageDetailsState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialWorkflowUsageDetailsState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setWorkflowUsageDetailsSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: WorkflowUsageDetailsSearchFiltersState) => {
        const { rpServiceId, workflowId, endDate, startDate } = newSearchFilters;

        dispatch(setWorkflowUsageDetailsSearchFilters(newSearchFilters));

        getWorkflowUsageDetailsSearchResultsReduxRequest(dispatch)(
            rpServiceId,
            workflowId,
            startDate,
            endDate,
            referenceData.axnServiceOfferings
        );
    };

    const sankeyChartTitle = useMemo(() => {
        if (referenceData.simpleAXNRPServices && searchFilters.rpServiceId) {
            const service = referenceData.simpleAXNRPServices.find(rps => rps.id === searchFilters.rpServiceId);
            return service?.serviceName || "";
        } else if (referenceData.simpleAXNWorkflows && searchFilters.workflowId) {
            const workflow = referenceData.simpleAXNWorkflows.find(w => w.id === searchFilters.workflowId);
            return workflow?.name || "";
        } else {
            return "";
        }
    }, [
        referenceData.simpleAXNRPServices,
        referenceData.simpleAXNWorkflows,
        searchFilters.rpServiceId,
        searchFilters.workflowId
    ]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowUsageDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <WorkflowUsageDetailsSearchForm
                        requestInProgress={searchResults.isLoading}
                        companies={referenceData.axnCompanies}
                        relyingParties={referenceData.axnRelyingParties}
                        rpServices={allowedServices}
                        workflows={allowedWorkflows}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.workflowResultsDetails && (
                    <CardContainer>
                        <ContentPageHeader title={resources.tables.serviceOfferingTransactionsSummary.title} />
                        <ContentPageBody isMaxWidth={true}>
                            <ServiceOfferingTransactionsSummaryTableContainer
                                workflowResultsDetails={searchResults.workflowResultsDetails}
                            />
                        </ContentPageBody>
                    </CardContainer>
                )}
                {isLoaded && !searchResults.isLoading && searchResults.workflowResultsDetails && (
                    <CardContainer>
                        <ContentPageHeader title={resources.tables.userTransactionsSummary.title} />
                        <ContentPageBody isMaxWidth={true}>
                            <UserTransactionsSummaryTableContainer
                                workflowResultsDetails={searchResults.workflowResultsDetails}
                            />
                        </ContentPageBody>
                    </CardContainer>
                )}
                {isLoaded && !searchResults.isLoading && searchResults.workflowSankeyResults?.length > 0 && (
                    <CardContainer>
                        <ContentPageBody isMaxWidth={true}>
                            <WorkflowSankeyDiagram
                                title={sankeyChartTitle}
                                data={searchResults.workflowSankeyResults}
                            />
                        </ContentPageBody>
                    </CardContainer>
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};